/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: fix types

import { useState } from 'react';
import { Project as ProjectType } from '@material-bank/projects-api';
import { useCurrentProject } from '@material-bank/projects-utils';
import { getUserLang } from '@mb/lib';
import { sendViewSearchResultsAnalyticsEvent } from '@mb/lib/analytics';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { StatusEnum as SnackbarStatusEnum } from 'components/components/MBSnackbar';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import startsWith from 'lodash/startsWith';
import uniq from 'lodash/uniq';
import xor from 'lodash/xor';
import { useDispatch, useSelector } from 'react-redux';
import { extraFieldsList, isBannersSearch } from './searConsts';
import {
  addAdditionalUserData,
  addFiltersAndSort,
  getBrandFilters,
  getSuggestionParams,
  processSearchSpringResponse,
} from './searcServiseUtils';
import { BodyPayloadType } from './types';

import {
  familyToggle,
  individualToggle,
} from '../components/Filter/FamilyViewSwitch';
import { config } from '../config';
import { searchActionsEnum } from '../config/constants';
import { useFiltersContext } from '../contexts';
import {
  appFiltersSelector,
  reduxAdditionalFiltersSelector,
} from '../redux/selectors/filtersSelector';
import {
  collectionHeaderSelector,
  isSearchQueryRelaxedSelector,
  xSessionIdSelector,
} from '../redux/selectors/searchSelector';
import { currentSortOptionSelector } from '../redux/selectors/searchSortingSelector';
import { setSnackbarInfo } from '../redux/sliceCreators/commonSlice';
import {
  addFilter,
  setAdditionalFilters,
  setAppFilters,
  setIsColorCircleTopbarShown,
  setOverrideParams,
  setSearchToggle,
  setTaxonomyFilterValue,
} from '../redux/sliceCreators/filtersSlice';
import {
  setCampaignBanners,
  setCollectionHeader,
  setIsSearchQueryRelaxed,
  setIsSearchRedirect,
  setXRequestId,
  setXSessionId,
} from '../redux/sliceCreators/searchSlice';
import { ProductSearchResponse } from '../types/apiResponse';
import {
  addCookiesFilters,
  getUrlParameter,
  transformSearchspringResponseToAppFormat,
} from '../utils';
import {
  getCharsNumber,
  isHomePage,
  mapV2ChildrenFilters,
  pushFilterAnalytics,
  setQueryToUrl,
} from '../utils/helpers';
import { getUserAttributes } from '../utils/localstorage';

const useSearchService = (overwriteSearchQuery: any) => {
  const dispatch = useDispatch();
  const { parseFilter, getNewFilters } = useFiltersContext();
  const currentSortOption = useSelector(currentSortOptionSelector);
  const appliedFilters = useSelector(appFiltersSelector);
  const isSearchQueryRelaxed = useSelector(isSearchQueryRelaxedSelector);
  const reduxAdditionalFilters = useSelector(reduxAdditionalFiltersSelector);
  const collectionHeader = useSelector(collectionHeaderSelector);
  const {
    currentProject,
  }: {
    currentProject: ProjectType;
  } = useCurrentProject();
  const xSessionid = useSelector(xSessionIdSelector);
  const [prevSearchPayload, setPrevSearchPayload] = useState<
    BodyPayloadType | undefined
  >(undefined);
  const [prevFilters, setPrevFilters] = useState<
    { key: string; value: string }[]
  >([]);
  const userData = getUserAttributes();
  const [overrideBgFilter, setOverrideBgFilter] = useState<
    Record<string, unknown> | undefined
  >(undefined);

  type SearchConfig = {
    resultsPerPage?: number;
    currentPage?: number;
    action?: string;
    customerEmail?: string;
    [key: string]: any;
  };

  const search = async (
    searchOptions: any,
    config: SearchConfig = {},
    {
      searchToggle = undefined,
      isBrandToggle = undefined,
    }: { searchToggle: string | undefined; isBrandToggle: boolean | undefined },
    isNewQuerySearch: boolean,
    suggestionsParams: any,
    predefinedIds: string[] = [],
    additionalFilters = {},
  ) => {
    let payloadAdditionalFilters = reduxAdditionalFilters;
    const { query, residentialCommercial } = searchOptions;
    const { resultsPerPage, currentPage, action, customerEmail } = config;
    const isNewSearch =
      action === searchActionsEnum.searchBar || isNewQuerySearch;
    const filters = {
      //wipe all filters directly on search, to avoid race conditions when going through context updates
      appFilters: isNewSearch
        ? []
        : flatten(Object.values(appliedFilters).map(parseFilter)),
    };
    const isFromBrandsPage =
      window.location.href?.includes('filterValueLimiter');
    const isFromMegamenu = window.location.href?.includes('megaMenuLink');

    if (action === searchActionsEnum.searchBar) {
      dispatch(setAdditionalFilters(additionalFilters));
      payloadAdditionalFilters = additionalFilters;
    }

    const urlQuery = getUrlParameter('q');
    if (query !== urlQuery && !isHomePage()) {
      setQueryToUrl(query);

      const queryToFetchBanners =
        (suggestionsParams?.suggesterValue?.length ?? 0) > 0
          ? suggestionsParams?.suggesterValue
          : query;
      const customEvent = new CustomEvent('trigger-banners-fetch', {
        detail: queryToFetchBanners,
      });
      window.dispatchEvent(customEvent);
    }

    const searchParams = {
      query,
      resultsPerPage,
      filters,
      searchToggle,
      isBrandToggle,
      action,
      page: currentPage ?? 1,
      customerEmail,
      residentialCommercial,
      suggestionsParams,
      predefinedIds,
      xSessionid,
      additionalFilters: payloadAdditionalFilters,
      appliedFilters,
      isFromBrandsPage,
      isFromMegamenu,
    };
    return apiSearch(searchParams);
  };

  const apiSearch = async (searchParams: any) => {
    const searchURL = config?.searchSpring.searchURL;
    const siteId = config?.searchSpring?.siteId;
    try {
      const {
        query,
        page,
        resultsPerPage,
        filters,
        action,
        searchToggle,
        isBrandToggle,
        customerEmail,
        residentialCommercial,
        suggestionsParams,
        xSessionid,
        additionalFilters,
        appliedFilters,
        isFromBrandsPage,
        isFromMegamenu,
      } = searchParams;

      const { appFilters } = filters;
      const searchForFamilies = searchToggle !== individualToggle;
      const isNewSearch = action === searchActionsEnum.searchBar;
      const isReset = action === searchActionsEnum.reset;
      const isResetFilters =
        action === searchActionsEnum.resetFilters || isReset;
      const filterVisibility =
        searchForFamilies || isNewSearch ? 'Catalog| Search' : 'Catalog';
      // remove the parent filter if has a child selected

      if (!searchForFamilies && isNewSearch) {
        // Reset Colorways filter on New Search
        dispatch(setSearchToggle(familyToggle));
      }

      let body: BodyPayloadType = {
        siteId,
        customerEmail,
        projectId: Number(userData?.current_project) || 0,
        projectType: currentProject?.type?.externalId
          ? String(currentProject.type.externalId)
          : userData.current_project_type,
        resultsPerPage:
          Math.round(resultsPerPage) || config.search.resultsPerPage,
        q: query,
        page: Number(page) ?? 1,
        referrerUrl: window.location.host + window.location.pathname,
        extraFields: extraFieldsList,
      };

      // Include segmentation data by default
      body = addAdditionalUserData(body, userData, currentProject);

      body = addFiltersAndSort(
        body,
        appFilters,
        filters,
        filterVisibility,
        currentSortOption,
        isNewSearch,
        residentialCommercial,
      );

      if (body?.bgfilter?.['country_permission']) {
        body['bgfilter.country_permission'] =
          body?.bgfilter['country_permission'] || '';
      }

      if (body.bgfilter?.['state_permission']) {
        body['bgfilter.state_permission'] =
          body?.bgfilter['state_permission'] || '';
      }

      delete body['bgfilter'];

      // Limit values, when coming from Brands page
      body['filterValueLimiter'] = isFromBrandsPage;

      body['relaxedQuery'] = isSearchQueryRelaxed && !isNewSearch;

      body['lang'] = `${getUserLang()}-${ENVIRONMENT_REGION || 'US'}`;

      const suggestionsParamsExtracted = getSuggestionParams(suggestionsParams);
      const brandFilters = getBrandFilters();
      body = {
        ...body,
        ...additionalFilters,
        ...brandFilters,
        ...suggestionsParamsExtracted,
      };

      addCookiesFilters(body, isBrandToggle);

      const {
        sessionId: bodySessionId,
        relaxedQuery = null,
        ...bodyRest
      } = body;
      const {
        sessionId: prevSessionId,
        relaxedQuery: prevRelaxedQuery = null,
        ...prevSearchPayloadRest
      } = prevSearchPayload ?? {}; // Add a fallback to an empty object if prevSearchPayload is undefined

      const ifInit = action === searchActionsEnum.init;

      if (
        isEqual(bodyRest, prevSearchPayloadRest) &&
        !isNewSearch &&
        !isBannersSearch
      ) {
        return;
      }
      if (
        Boolean(xSessionid) &&
        ((!isNewSearch && !isResetFilters && !isFromMegamenu) || ifInit)
      ) {
        body['sessionId'] = xSessionid;
        if (overrideBgFilter != null) {
          Object.keys(overrideBgFilter).forEach((key) => {
            body[key] =
              key === 'bgfilter.visibility' &&
              Array.isArray(overrideBgFilter[key])
                ? overrideBgFilter?.[key]?.[0]
                : overrideBgFilter[key];
          });
        }
      } else {
        // if sessionId is not set, we need to clear the overrideBgFilter, since it is new session
        setOverrideBgFilter(undefined);
      }
      if (!isBannersSearch) {
        setPrevSearchPayload(body);
      }
      if (isReset) {
        window.dispatchEvent(
          new CustomEvent('searchFilterRemove', {
            detail: { isReset: true },
          }),
        );
      }

      dispatch(setTaxonomyFilterValue(undefined));

      const bodyFilters = pickBy(body, (value, key) => {
        return startsWith(key, 'filter.');
      });

      const childrenFilters = mapV2ChildrenFilters(bodyFilters);

      const childrenBody = {
        customer: {
          id: String(userData?.customer_id) || null,
          email: userData?.email,
          country: userData?.country,
          province: userData?.state,
        },
        siteId,
        lang: `${getUserLang()}-${ENVIRONMENT_REGION || 'US'}`,
        incognito: true,
        esQuery: false,
        resultsPerPage:
          Math.round(resultsPerPage) || config.search.resultsPerPage,
        page: Number(page) ?? 1,
        routeId: window.location.pathname?.split('/')?.pop(),
        filters: childrenFilters || [],
        extraFields: extraFieldsList,
      };

      const finalUrl = isBannersSearch
        ? config.searchSpring.children
        : searchURL;
      const finalBody: Partial<BodyPayloadType> = isBannersSearch
        ? childrenBody
        : body;
      let data: ProductSearchResponse = {};
      try {
        data = await fetch(finalUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(finalBody),
        }).then((res) => {
          if (!res.ok) {
            throw new Error('Error fetch search results');
          }
          const xRequestId = res.headers.get('x-request-id');
          if (xRequestId != null) {
            dispatch(setXRequestId(xRequestId));
            if (
              ifInit ||
              isNewSearch ||
              isResetFilters ||
              xSessionid == null ||
              isFromMegamenu
            ) {
              dispatch(setXSessionId(xRequestId));
            }
          }
          return res.json();
        });
      } catch (e) {
        console.log(e);
        if (isBannersSearch) {
          dispatch(
            setSnackbarInfo({
              isOpen: true,
              message: 'Collection not found',
              status: SnackbarStatusEnum.DANGER,
            }),
          );
          handleSystemErrorReport(
            `Collection not found: ${e.message}`,
            'fetchSearchspringResponse',
          );
          const userLang = getUserLang();
          const env = ENVIRONMENT_REGION || 'US';
          if (env === 'EU') {
            window.location.href = `/${userLang}/curatedcollections`;
          } else {
            window.location.href = '/curatedcollections';
          }
        }
      }
      const searchType =
        getUrlParameter('searchType') || suggestionsParams?.searchType;

      // Analytics from Searchbar
      if (searchType != null) {
        sendViewSearchResultsAnalyticsEvent({
          query: body['suggesterValue'] || body['q'],
          searchType,
          resultsNumber: data?.pagination?.totalResults || 0,
        });
      }

      //
      data = processSearchSpringResponse(data, body);

      if (data?.redirectUrl != null) {
        window.location.replace(data?.redirectUrl);
        //this will block the execution of JS to not show an empty page before redirection takes place

        // @ts-ignore
        await new Promise(() => {});
      }

      dispatch(setCampaignBanners(data?.campaign?.banners ?? []));

      if (data?.results == null) {
        return { ...data, results: [] };
      }

      if (data?.header?.[0] != null && collectionHeader == null) {
        dispatch(setCollectionHeader(data?.header?.[0]));
      }

      const results = data.results.map(
        transformSearchspringResponseToAppFormat,
      );

      const addNewFilters = (
        keyValueArr: { key: string; value: unknown }[] = [],
      ) => {
        const newFilters = flatten(
          keyValueArr?.map((item: { key: string; value: string }) =>
            getNewFilters(item.key, item.value, appFilters),
          ),
        );
        if (newFilters?.length > 0) {
          dispatch(addFilter(newFilters));
        }
      };

      const allowedOverrides = [
        'filter',
        'q',
        'q_original',
        'sort',
        'q_rewritten',
      ];
      let currentSelectedColor = finalBody?.['filter.virtual_color_group'];

      const paramsOverrides = data?.overrides ?? data?.paramsOverrides;
      const newFilters: { key: string; value: unknown }[] = [];
      let newSearchToggle = null;

      if (!isEmpty(additionalFilters)) {
        Object.entries(additionalFilters).forEach(([key, value]) => {
          newFilters.push({ key, value });
        });
      }
      if (paramsOverrides) {
        //iterate over the params override
        const filteredParams = Object.fromEntries(
          Object.entries(paramsOverrides)?.filter(([key]) => {
            const mainKey = key.split('.')?.[0];
            return (
              allowedOverrides.includes(mainKey) &&
              !key.includes('color_data') &&
              key.startsWith('filter.')
            );
          }),
        );
        dispatch(
          setOverrideParams(
            isFromBrandsPage
              ? null
              : isEmpty(filteredParams)
              ? null
              : filteredParams,
          ),
        );
        const filteredBgOverrides = Object.fromEntries(
          Object.entries(paramsOverrides).filter(([key]) =>
            key.startsWith('bgfilter.'),
          ),
        );
        setOverrideBgFilter(filteredBgOverrides);
        Object.entries(paramsOverrides).forEach(([key, value]) => {
          //and handle only the filters
          if (key.startsWith('filter.') && !key.includes('filter.color_data')) {
            if (value != null) {
              if (key.includes('taxonomy')) {
                const currentValue = Array.isArray(value) ? value?.[0] : value;
                // If subcategory comes as filter, make sure to add parent as well for better toggle functionality
                const taxonomyLevel = getCharsNumber(currentValue, '>');
                const taxonomyValue = Array.isArray(value) ? value : [value];
                const isMultipleParents =
                  uniq(taxonomyValue?.map((item) => item.split('>')?.[1]) || [])
                    ?.length > 1;
                if (taxonomyLevel === 2 && !isMultipleParents) {
                  const levels = currentValue?.split('>');
                  levels.pop();
                  const parent = levels.join('>');
                  newFilters.push({ key, value: parent }, { key, value });
                } else {
                  newFilters.push({ key, value });
                }
              } else {
                newFilters.push({ key, value });
              }
            } else {
              const filtersToRemove = Object.keys(appliedFilters).filter(
                (item) => item.includes(key),
              );
              if (filtersToRemove?.length > 0) {
                const clearedFilters = omit(appliedFilters, filtersToRemove);
                dispatch(setAppFilters(clearedFilters));
              }
            }
          }
          if (key === 'filter.virtual_color_group') {
            currentSelectedColor = value;
            dispatch(setIsColorCircleTopbarShown(true));
          }
          if (key === 'q') {
            setNewQuery(overwriteSearchQuery, query, String(value));
          }
          if (key === 'bgfilter.visibility') {
            newSearchToggle =
              value === 'Catalog' ? familyToggle : individualToggle;
          }
          if (key === 'is_relaxedquery') {
            dispatch(setIsSearchQueryRelaxed(value));
          }
          if (key === 'is_redirect') {
            dispatch(setIsSearchRedirect(value));
          }
        });
      } else {
        // If no params overrides, we need to clear the overrideBgFilter
        setOverrideBgFilter(undefined);
      }
      if (newFilters?.length > 0) {
        addNewFilters(newFilters);
      }
      if (newSearchToggle != null) {
        dispatch(setSearchToggle(newSearchToggle));
      }

      // Applied Filter Analytics
      const overrideFilters = newFilters?.map(
        ({ key, value }: { key: string; value: string }) => `${key}=${value}`,
      );
      const currentFilters = [...appFilters, ...overrideFilters].filter(
        (item) => !item.includes('price_sign_filter.low'),
      );
      const prevFilterLength = prevFilters?.length;
      const filterLength = currentFilters?.length;

      if (
        (filterLength !== prevFilterLength ||
          !isEqual(prevFilters, currentFilters)) &&
        !isFromMegamenu
      ) {
        const diffFilters = xor(prevFilters, currentFilters);
        const isAdded = filterLength >= prevFilterLength;
        const isClearedAll =
          action === searchActionsEnum.reset &&
          prevFilterLength - filterLength > 1;
        pushFilterAnalytics({
          resultsNumber: data.pagination?.totalResults || 0,
          isReset: isClearedAll,
          filter: diffFilters?.pop(),
          isAdded,
        });
      }

      setPrevFilters(currentFilters);

      const contentBlocks = data?.contentBlocks?.results ?? [];

      return {
        ...data,
        results,
        contentBlocks,
        isFromMegamenu,
        currentSelectedColor,
      };
    } catch (error) {
      handleSystemErrorReport(
        `Error fetchSearchspringResponse: ${error.message}`,
        'fetchSearchspringResponse',
      );
      console.error({ error });
      return {
        results: [],
        contentBlocks: [],
        isFromMegamenu: false,
        currentSelectedColor: null,
      };
    }
  };

  return { overrideBgFilter, search, apiSearch };
};

function setNewQuery(
  overwriteFn: (value: string) => void,
  prevQuery: string,
  newValue: string,
) {
  const setSearchInputEvent = new CustomEvent('searchQueryResultsChange', {
    detail: {
      query: newValue,
    },
  });
  document.dispatchEvent(setSearchInputEvent);
  if (prevQuery !== newValue) {
    overwriteFn(newValue);
    const newHistoryPath = window.history.state?.path?.replace(
      `q=${prevQuery?.replaceAll(' ', '+')}`,
      `q=${newValue}`,
    );
    window.history.pushState(
      {
        path: newHistoryPath,
      },
      document.title,
      newHistoryPath,
    );
  }
}

export { useSearchService };
